/**
 * Universeller Vergleichs-Kachel-Renderer.
 *
 * Nimmt einen `AktionComparisonSlot` (siehe useAktionsSeite.ts) und
 * rendert je nach Konfiguration:
 *   • Single-Item:    genau ein Medium (Foto / Video / 360°-Foto / 360°-Video)
 *   • Slideshow:      rotiert alle `interval_seconds` durch die Items,
 *                     Cross-Fade 300 ms.
 *
 * Wichtige Details:
 *   • IntersectionObserver: die Slideshow pausiert, wenn die Kachel
 *     nicht im Viewport ist (spart Rechenzeit + Bandbreite bei off-screen
 *     Ads-Traffic).
 *   • 360°-Viewer werden per `key` neu gemountet — dadurch werden
 *     WebGL-Kontexte sauber destroyed und keine Leaks über mehrere
 *     Slides hinweg.
 *   • Videos sind autoplay + muted + loop (Browser-konforme Regeln).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Video360Viewer from "@/components/Video360Viewer";
import Photo360Viewer from "@/components/Photo360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import type { AktionComparisonItem, AktionComparisonSlot } from "@/lib/useAktionsSeite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** URL für ein Item ermitteln: Media-ID hat Vorrang vor freier src. */
function resolveSrc(item: AktionComparisonItem): string {
    if (item.media_id && (item.media_type === "video_360" || item.media_type === "photo_360" || item.media_type === "video")) {
        return `${BACKEND_URL}/api/media/${item.media_id}/file`;
    }
    if (item.media_id && item.media_type === "image") {
        return `${BACKEND_URL}/api/media/${item.media_id}/file`;
    }
    return item.src || "";
}

/**
 * Video-Slide für die Slideshow.
 * - Im **Standalone-Modus** (keine Slideshow): autoplay + muted + loop.
 * - Im **Slideshow-Modus**: nur beim aktiven Slide abspielen (kein Loop),
 *   `onEnded` triggert `advance()` im Parent. Beim Deaktivieren pausieren
 *   und `currentTime = 0` — sodass beim erneuten Aufruf sauber von vorn
 *   gestartet wird.
 */
function SlideVideo({
    item,
    testIdPrefix,
    isActive,
    onEnded,
    inSlideshow,
}: {
    item: AktionComparisonItem;
    testIdPrefix: string;
    isActive: boolean;
    onEnded?: () => void;
    inSlideshow: boolean;
}) {
    const src = resolveSrc(item);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        if (!inSlideshow) return; // im Standalone-Modus greift Browser-autoplay
        const v = videoRef.current;
        if (!v) return;
        if (isActive) {
            try { v.currentTime = 0; } catch { /* Firefox wirft manchmal InvalidStateError beim reset */ }
            void v.play().catch(() => undefined);
        } else {
            v.pause();
            try { v.currentTime = 0; } catch { /* ignore */ }
        }
    }, [isActive, inSlideshow]);

    return (
        <video
            ref={videoRef}
            src={src}
            poster={item.poster || undefined}
            autoPlay={!inSlideshow || isActive}
            muted
            loop={!inSlideshow}
            playsInline
            preload="auto"
            onEnded={() => { if (inSlideshow) onEnded?.(); }}
            className="w-full h-full"
            style={{ objectFit: "cover", display: "block" }}
            data-testid={`${testIdPrefix}-video`}
        />
    );
}

function ItemRenderer({
    item, testIdPrefix, isActive = true, onEnded, inSlideshow = false,
}: {
    item: AktionComparisonItem;
    testIdPrefix: string;
    isActive?: boolean;
    onEnded?: () => void;
    inSlideshow?: boolean;
}) {
    const src = resolveSrc(item);
    if (item.media_type === "image") {
        // `object-fit: cover` → Kachel voll gefüllt, kein Cream-Letterbox.
        // `object-position: center 30%` biast leicht nach oben, sodass der
        // typischerweise mitte-oben sitzende Bilderrahmen in den Vintage-
        // Aufnahmen erhalten bleibt.
        return (
            <img
                src={src}
                alt=""
                className="w-full h-full"
                style={{ objectFit: "cover", objectPosition: "center 30%", display: "block" }}
                loading="lazy"
                decoding="async"
                data-testid={`${testIdPrefix}-image`}
            />
        );
    }
    if (item.media_type === "video") {
        return (
            <SlideVideo
                item={item}
                testIdPrefix={testIdPrefix}
                isActive={isActive}
                onEnded={onEnded}
                inSlideshow={inSlideshow}
            />
        );
    }
    if (item.media_type === "photo_360") {
        // 360°-Foto: WebGL-Viewer nutzt die volle Container-Fläche (fillParent).
        return (
            <ViewerErrorBoundary testId={`${testIdPrefix}-photo360-fallback`} fallbackTitle="360°-Foto kann nicht geladen werden">
                <Photo360Viewer
                    src={src}
                    mediaId={item.media_id}
                    mediaTitle="Aktions-Vergleichs-Foto"
                    fillParent
                />
            </ViewerErrorBoundary>
        );
    }
    // video_360
    return (
        <ViewerErrorBoundary testId={`${testIdPrefix}-video360-fallback`} fallbackTitle="360°-Video kann nicht geladen werden">
            <Video360Viewer
                src={src}
                mediaId={item.media_id}
                mediaTitle="Aktions-Vergleichs-Video"
                testIdPrefix={testIdPrefix}
                autoPlay
                autoTour
                fillParent
                // In Slideshows: kein Loop, `ended` treibt die Rotation weiter,
                // und `isActive` steuert Reset+Play beim Slide-Wechsel.
                loop={!inSlideshow}
                onEnded={inSlideshow ? onEnded : undefined}
                isActive={inSlideshow ? isActive : true}
            />
        </ViewerErrorBoundary>
    );
}

type Props = {
    slot: AktionComparisonSlot;
    /** Test-ID-Präfix für die einzelnen Items. */
    testIdPrefix: string;
    /** Optional: Container-Klassen. Default: dunkler Passepartout. */
    containerBg?: string;
};

export default function AktionComparisonSlot({ slot, testIdPrefix, containerBg = "#0F1830" }: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [inView, setInView] = useState(true);
    const items = slot.items && slot.items.length > 0 ? slot.items : [];
    const isSlideshow = slot.slideshow_enabled && items.length > 1;
    const intervalMs = Math.max(1, Math.min(30, slot.interval_seconds || 3)) * 1000;
    const activeItem = useMemo(() => items[currentIdx] || items[0], [items, currentIdx]);
    // Wenn der aktive Slide ein Video ist (normal oder 360°): kein Interval-
    // Timer, stattdessen wartet die Slideshow auf das `ended`-Event
    // (siehe onEnded → advance()).
    const activeIsVideo = activeItem?.media_type === "video" || activeItem?.media_type === "video_360";

    // IntersectionObserver: pause bei off-screen.
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const obs = new IntersectionObserver(
            (entries) => {
                for (const e of entries) setInView(e.isIntersecting);
            },
            { threshold: 0.15 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const advance = () => setCurrentIdx((i) => (i + 1) % items.length);

    // Slideshow-Timer — nur für nicht-Video-Slides.
    useEffect(() => {
        if (!isSlideshow || !inView) return;
        if (activeIsVideo) return; // Video steuert den Wechsel selbst via `ended`
        const id = window.setInterval(() => {
            setCurrentIdx((i) => (i + 1) % items.length);
        }, intervalMs);
        return () => window.clearInterval(id);
    }, [isSlideshow, inView, intervalMs, items.length, activeIsVideo]);

    // Reset currentIdx wenn items geändert werden.
    useEffect(() => {
        if (currentIdx >= items.length) setCurrentIdx(0);
    }, [items.length, currentIdx]);

    if (items.length === 0) {
        return (
            <div
                ref={containerRef}
                className="w-full h-full flex items-center justify-center text-sm"
                style={{ background: containerBg, color: "#F1F5F9" }}
                data-testid={`${testIdPrefix}-empty`}
            >
                (Kein Medium konfiguriert)
            </div>
        );
    }

    if (!isSlideshow) {
        return (
            <div ref={containerRef} className="w-full h-full overflow-hidden" style={{ background: containerBg }} data-testid={testIdPrefix}>
                <ItemRenderer item={activeItem} testIdPrefix={testIdPrefix} />
            </div>
        );
    }

    // Slideshow-Modus mit Cross-Fade.
    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden"
            style={{ background: containerBg }}
            data-testid={testIdPrefix}
        >
            {items.map((it, i) => (
                <div
                    key={i}
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{
                        opacity: i === currentIdx ? 1 : 0,
                        pointerEvents: i === currentIdx ? "auto" : "none",
                    }}
                    aria-hidden={i !== currentIdx}
                >
                    {/* Wichtig: 360°-Viewer bekommen einen stabilen Key aus (i + src),
                     *  damit sie sauber gemounted/destroyed werden. Bilder/Videos
                     *  bleiben durchgängig gemountet, was das Cross-Fade sauber macht. */}
                    <ItemRenderer
                        key={`${i}-${it.src || it.media_id}`}
                        item={it}
                        testIdPrefix={`${testIdPrefix}-slide-${i}`}
                        isActive={i === currentIdx}
                        onEnded={advance}
                        inSlideshow
                    />
                </div>
            ))}
            {/* Kleine Slide-Indikator-Dots unten mittig. */}
            <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-1.5 pointer-events-none z-10">
                {items.map((_, i) => (
                    <span
                        key={i}
                        aria-hidden
                        className="rounded-full transition-all"
                        style={{
                            width: i === currentIdx ? 18 : 6,
                            height: 6,
                            background: i === currentIdx ? "#D4A855" : "rgba(255,255,255,0.6)",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
